import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';
import { getMember, selectSelectedMember } from 'redux/slices/memberSlice';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { MemberPage } from './MemberPage';
import UnauthorizedMemberAccess from './UnauthorizedMemberAccess';

/**
 * Wrapper component that checks member access and shows UnauthorizedMemberAccess
 * if the user doesn't have access to the member (404 with access error message)
 */
const ProtectedMemberPage = () => {
  const { memberId } = useParams<{ memberId: string }>();
  const dispatch = useAppDispatch();
  const selectedMember = useAppSelector(selectSelectedMember);
  const isAuthenticated = actingUserAppSelectors.useSelectIsAuthenticated();
  const [hasAccessError, setHasAccessError] = useState<boolean>(false);

  useEffect(() => {
    // Only check access if we have a memberId and user is authenticated
    if (!memberId || !isAuthenticated) {
      return;
    }

    // If member is already loaded and matches, no access error
    if (selectedMember?.id === memberId) {
      setHasAccessError(false);
      return;
    }

    // Try to fetch the member to check access
    const checkAccess = async () => {
      setHasAccessError(false);

      const result = await dispatch(getMember({ params: { id: memberId } }));

      if (getMember.rejected.match(result)) {
        const errorCode = result.payload?.code;
        // The payload is CustomSerializedError, but ApiRequestError has a body property
        // We need to cast it to access the body
        const errorPayload = result.payload as {
          code?: string;
          body?: { userMessage?: string };
        };
        const userMessage = errorPayload?.body?.userMessage;

        // Check for 404 with access error message, or 401 (for authenticated users, 401 on member paths means no access)
        const isAccessError =
          (errorCode === '404' &&
            userMessage?.includes('does not have access to member')) ||
          (errorCode === '401' && isAuthenticated);

        if (isAccessError) {
          setHasAccessError(true);
        }
      }
    };

    void checkAccess();
  }, [memberId, isAuthenticated, dispatch, selectedMember?.id]);

  // Show unauthorized component if access was denied
  if (hasAccessError) {
    return <UnauthorizedMemberAccess />;
  }

  // Otherwise, show the normal member page
  return <MemberPage />;
};

export default ProtectedMemberPage;
